import React, { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * The brand's menu, to whatever depth they built it.
 *
 * A menu used to be one flat row, so a shop with categories had to put every
 * category in the bar or leave them out. Links now carry children, and a link
 * with children opens a panel — as does a link inside that panel, for as deep
 * as the owner went.
 *
 * Plain elements and inline styles like the rest of the public frame: this
 * renders on the brand's own domain, outside our theme, and must show the
 * colours they chose rather than anything inherited from us.
 *
 * Open on hover AND on focus. Hover alone is a menu nobody can reach from a
 * keyboard, and these are often the only way to the pages underneath.
 */

const MENU_VARIANTS = {
  plain: () => ({}),
  underline: (active, color) => ({
    borderBottom: `2px solid ${active ? color : 'transparent'}`,
    paddingBottom: 2,
  }),
  pill: (active, color) => ({
    padding: '6px 12px',
    borderRadius: 999,
    background: active ? `${color}1a` : 'transparent',
  }),
  bordered: (active, color) => ({
    padding: '5px 11px',
    borderRadius: 8,
    border: `1px solid ${active ? color : 'transparent'}`,
  }),
};

const linkStyle = (style, color, active) => ({
  color,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  fontSize: style.fontSize,
  fontWeight: style.bold ? 600 : 500,
  letterSpacing: style.letterSpacing ? `${style.letterSpacing}px` : undefined,
  textTransform: style.uppercase ? 'uppercase' : 'none',
  opacity: active ? 1 : 0.85,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  transition: 'opacity 120ms ease',
  ...(MENU_VARIANTS[style.menuVariant] || MENU_VARIANTS.plain)(active, color),
});

/** The little chevron that says "there is more under this". */
const Caret = ({ open, side }) => (
  <span
    aria-hidden='true'
    style={{
      display: 'inline-block',
      fontSize: 9,
      lineHeight: 1,
      opacity: 0.7,
      transform: side ? 'rotate(-90deg)' : open ? 'rotate(180deg)' : 'none',
      transition: 'transform 140ms ease',
    }}
  >
    ▼
  </span>
);

function NavItem({ link, style, color, depth }) {
  const [open, setOpen] = useState(false);
  const children = (link.children || []).filter((c) => c.label && c.url);
  const hasChildren = children.length > 0;

  // A parent whose only job is to open a panel carries '#' as its address.
  // Following that would jump the page to the top for no reason.
  const isHeadingOnly = hasChildren && (!link.url || link.url === '#');

  const panel = {
    position: 'absolute',
    // A top-level menu drops down; anything deeper opens to the side, which
    // is the only direction left once a panel is already against the bar.
    ...(depth === 0 ? { top: '100%', left: 0 } : { top: -6, left: '100%' }),
    minWidth: 190,
    padding: '6px',
    margin: 0,
    listStyle: 'none',
    borderRadius: 12,
    // Its own surface rather than the bar's: a dropdown over page content
    // needs to be readable against whatever is behind it, which a
    // translucent header deliberately is not.
    background: style.panelBackground,
    color,
    border: '1px solid rgba(127,127,127,0.18)',
    boxShadow: '0 12px 28px rgba(0,0,0,0.16)',
    zIndex: 60,
  };

  return (
    <li
      style={{ position: 'relative', listStyle: 'none' }}
      onMouseEnter={() => hasChildren && setOpen(true)}
      onMouseLeave={() => hasChildren && setOpen(false)}
      onFocus={() => hasChildren && setOpen(true)}
      onBlur={(e) => {
        // Only close once focus has left the whole branch, or tabbing into a
        // submenu would shut the panel being tabbed into.
        if (hasChildren && !e.currentTarget.contains(e.relatedTarget)) setOpen(false);
      }}
    >
      {isHeadingOnly ? (
        <button
          type='button'
          aria-haspopup='true'
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          style={{
            ...linkStyle(style, color, open),
            background: 'none',
            border: (MENU_VARIANTS[style.menuVariant] || MENU_VARIANTS.plain)(open, color).border
              || 'none',
            cursor: 'pointer',
            font: 'inherit',
            fontSize: style.fontSize,
          }}
        >
          {link.label}
          <Caret open={open} side={depth > 0} />
        </button>
      ) : (
        <Link
          to={link.url}
          target={link.newTab ? '_blank' : undefined}
          rel={link.newTab ? 'noopener noreferrer' : undefined}
          aria-haspopup={hasChildren ? 'true' : undefined}
          aria-expanded={hasChildren ? open : undefined}
          style={linkStyle(style, color, open)}
        >
          {link.label}
          {hasChildren && <Caret open={open} side={depth > 0} />}
        </Link>
      )}

      {hasChildren && open && (
        <ul style={panel}>
          {children.map((child, i) => (
            <li key={i} style={{ listStyle: 'none', position: 'relative' }}>
              <div style={{ padding: '7px 10px', borderRadius: 8 }}>
                <NavItem link={child} style={style} color={color} depth={depth + 1} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export default function SiteNav({ links, style, color, justify = 'flex-start' }) {
  const items = (links || []).filter((l) => l.label && l.url);
  if (items.length === 0) return null;

  return (
    <nav>
      <ul
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: style.linkGap,
          flexWrap: 'wrap',
          justifyContent: justify,
          listStyle: 'none',
          margin: 0,
          padding: 0,
        }}
      >
        {items.map((link, i) => (
          <NavItem key={i} link={link} style={style} color={color} depth={0} />
        ))}
      </ul>
    </nav>
  );
}
